import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../services/api';

interface Statistics {
  project_id: number;
  project_name: string;
  client_name: string;
  total_jobs: number;
  jobs_by_status: {
    completed: number;
    processing: number;
    pending: number;
    failed: number;
  };
  jobs_by_system: {
    [key: string]: number;
  };
  outputs: {
    [key: string]: number;
  };
  total_outputs: number;
}

interface Chart {
  type: string;
  title: string;
  image: string;
}

const chartTitles: { [key: string]: string } = {
  jobs_by_status: 'Jobs by Status',
  jobs_by_system: 'Jobs by System',
  outputs_by_system: 'Outputs by System',
};

export const AnalyticsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [charts, setCharts] = useState<Chart[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (projectId) {
      fetchAnalytics();
    }
  }, [projectId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const statsResponse = await api.get(`/analytics/projects/${projectId}/statistics`);
      setStatistics(statsResponse.data);

      const chartsResponse = await api.get(`/analytics/projects/${projectId}/charts`);

      const chartsData = chartsResponse.data.charts;
      if (chartsData && typeof chartsData === 'object') {
        const chartsArray: Chart[] = Object.entries(chartsData)
          .filter(([_, value]) => value)
          .map(([key, value]) => ({
            type: key,
            title: chartTitles[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            image: value as string,
          }));
        setCharts(chartsArray);
      } else {
        setCharts([]);
      }
    } catch (err) {
      setError('Failed to load analytics. Please make sure the project has some jobs.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout projectId={projectId ? parseInt(projectId) : null}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="spinner text-brand-600 w-10 h-10 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading analytics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !statistics) {
    return (
      <Layout projectId={projectId ? parseInt(projectId) : null}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-danger-100 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-danger-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-danger-600 text-lg mb-4">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="btn-secondary"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to projects
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout projectId={parseInt(projectId!)} projectName={`${statistics.client_name} - ${statistics.project_name}`}>
      <div className="animate-fade-in">
        {/* Page Header */}
        <div className="page-header">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Analytics Dashboard</h1>
              <p className="page-subtitle">{statistics.client_name} - {statistics.project_name}</p>
            </div>
            <button onClick={() => navigate('/')} className="btn-secondary">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Project
            </button>
          </div>
        </div>

        {/* Key Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="stat-card border-l-4 border-l-gray-400">
            <p className="stat-card-label">Total Jobs</p>
            <p className="stat-card-value">{statistics.total_jobs}</p>
          </div>
          <div className="stat-card border-l-4 border-l-success-500">
            <p className="stat-card-label">Completed</p>
            <p className="stat-card-value text-success-600">{statistics.jobs_by_status.completed}</p>
          </div>
          <div className="stat-card border-l-4 border-l-warning-500">
            <p className="stat-card-label">Processing</p>
            <p className="stat-card-value text-warning-600">{statistics.jobs_by_status.processing}</p>
          </div>
          <div className="stat-card border-l-4 border-l-danger-500">
            <p className="stat-card-label">Failed</p>
            <p className="stat-card-value text-danger-600">{statistics.jobs_by_status.failed}</p>
          </div>
        </div>

        {/* Outputs by System */}
        <div className="card mb-6">
          <div className="card-header">
            <h3 className="font-semibold text-gray-900">Total Outputs: {statistics.total_outputs}</h3>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-5 gap-4">
              <div className="text-center p-4 bg-brand-50 rounded-lg">
                <div className="w-10 h-10 mx-auto rounded-lg bg-brand-100 flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-600 text-sm">FactsAgent</p>
                <p className="text-2xl font-bold text-brand-600">{statistics.outputs.factsagent || 0}</p>
              </div>
              <div className="text-center p-4 bg-success-50 rounded-lg">
                <div className="w-10 h-10 mx-auto rounded-lg bg-success-100 flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                </div>
                <p className="text-gray-600 text-sm">TopicalMap</p>
                <p className="text-2xl font-bold text-success-600">{statistics.outputs.topicalmap || 0}</p>
              </div>
              <div className="text-center p-4 bg-accent-50 rounded-lg">
                <div className="w-10 h-10 mx-auto rounded-lg bg-accent-100 flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <p className="text-gray-600 text-sm">CPPages</p>
                <p className="text-2xl font-bold text-accent-600">{statistics.outputs.cppages || 0}</p>
              </div>
              <div className="text-center p-4 bg-pink-50 rounded-lg">
                <div className="w-10 h-10 mx-auto rounded-lg bg-pink-100 flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-gray-600 text-sm">Images</p>
                <p className="text-2xl font-bold text-pink-600">{statistics.outputs.images || 0}</p>
              </div>
              <div className="text-center p-4 bg-warning-50 rounded-lg">
                <div className="w-10 h-10 mx-auto rounded-lg bg-warning-100 flex items-center justify-center mb-2">
                  <svg className="w-5 h-5 text-warning-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <p className="text-gray-600 text-sm">Content</p>
                <p className="text-2xl font-bold text-warning-600">{statistics.outputs.content || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="card mb-6">
          <div className="card-header">
            <h3 className="font-semibold text-gray-900">Charts & Visualizations</h3>
          </div>
          <div className="card-body">
            {charts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {charts.map((chart, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                    <h4 className="font-semibold text-gray-700 mb-3">{chart.title}</h4>
                    <img
                      src={`data:image/png;base64,${chart.image}`}
                      alt={chart.title}
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <svg className="empty-state-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <h3 className="empty-state-title">No charts available</h3>
                <p className="empty-state-description">Run some jobs to see visualizations</p>
              </div>
            )}
          </div>
        </div>

        {/* Download Options */}
        {charts.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-gray-900">Download Reports</h3>
            </div>
            <div className="card-body">
              <div className="flex flex-wrap gap-3">
                {charts.map((chart, index) => (
                  <a
                    key={index}
                    href={`/api/analytics/projects/${projectId}/charts/${chart.type}`}
                    download={`${chart.type}.png`}
                    className="btn-primary"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {chart.title}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
